import { useMemo } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useInternalStepValidation } from './useInternalStepValidation';
import { useRequiredInfoValidation } from './useRequiredInfoValidation';
import { describeMissingRequiredInfo } from './requiredInfoValidation';

export type StepValidationResult = {
  isValid: boolean;
  missingFields: string[];
};

/** Keeps repeated address complaints (steps 0 and 1 both check them) out of tooltips. */
const dedupe = (fields: string[]): string[] => Array.from(new Set(fields));

/**
 * Validates whether a main step (1-5) holds enough data for the customer to move
 * past it. This is the authority behind every forward-navigation gate: the top
 * stepper, the header Next button, the summary bar tiles and Schedule Now.
 *
 * Required info used to be validated through
 * `useInternalStepValidation(2, 3)`, which defaulted `hasAddonStep` to false and
 * therefore returned "valid" without inspecting a single field — so any
 * navigation affordance let the customer skip the More Information step. Required
 * info is now resolved directly from the shared rule, independent of how many
 * internal steps the Property step happens to render for this company.
 */
export function useStepValidation(step: number): StepValidationResult {
  const { selectedDateTime, selectedInspectorForSlot, contact } = useScheduler();

  // Services step internal validations
  const step1Internal0 = useInternalStepValidation(1, 0);
  const step1Internal1 = useInternalStepValidation(1, 1);
  const step1Internal2 = useInternalStepValidation(1, 2);

  // Property step internal validations. Case 0 covers the address, case 1 the
  // address plus the required property details (Sq Ft / Year Built / Foundation).
  // Both are index-stable; required info is resolved separately below so it
  // cannot fall through the dynamic addon-step index mapping.
  const step2Internal0 = useInternalStepValidation(2, 0);
  const step2Internal1 = useInternalStepValidation(2, 1);

  const requiredInfo = useRequiredInfoValidation();

  return useMemo(() => {
    const missingFields: string[] = [];

    const collectServices = (prefix = '') => {
      [step1Internal0, step1Internal1, step1Internal2].forEach((validation) => {
        if (!validation.isValid) {
          missingFields.push(
            ...validation.missingFields.map((f) => `${prefix}${f}`)
          );
        }
      });
    };

    const collectProperty = (prefix = '') => {
      [step2Internal0, step2Internal1].forEach((validation) => {
        if (!validation.isValid) {
          missingFields.push(
            ...validation.missingFields.map((f) => `${prefix}${f}`)
          );
        }
      });
      // Addon recommendations are optional; required info is not.
      missingFields.push(
        ...describeMissingRequiredInfo(requiredInfo.missingItems).map(
          (f) => `${prefix}${f}`
        )
      );
    };

    const collectSchedule = () => {
      if (!selectedDateTime) {
        missingFields.push('Select a date and time');
      } else if (!selectedInspectorForSlot?.[selectedDateTime]) {
        missingFields.push('Select an inspector');
      }
    };

    const collectContact = () => {
      if (!contact) {
        missingFields.push('Enter contact information');
        return;
      }
      if (!contact.firstName) missingFields.push('First name');
      if (!contact.lastName) missingFields.push('Last name');
      if (!contact.email) missingFields.push('Email address');
      if (!contact.phone) missingFields.push('Phone number');
    };

    switch (step) {
      case 1: // Services step - check all internal steps
        collectServices();
        break;

      case 2: // Property step - address plus the required-info questionnaire
        collectProperty();
        break;

      case 3: // Schedule step - single step, validated at main level
        collectSchedule();
        break;

      case 4: // Contact step (Additional Contacts)
        // Additional contacts are deliberately optional ("Skip for Now"); only
        // the primary contact captured on the Services step is mandatory.
        collectContact();
        break;

      case 5: // Summary step - re-validate everything before booking is allowed
        collectServices('Services: ');
        collectProperty('Property: ');
        collectSchedule();
        collectContact();
        break;

      default:
        break;
    }

    const deduped = dedupe(missingFields);

    return {
      isValid: deduped.length === 0,
      missingFields: deduped,
    };
  }, [
    step,
    selectedDateTime,
    selectedInspectorForSlot,
    contact,
    requiredInfo,
    step1Internal0,
    step1Internal1,
    step1Internal2,
    step2Internal0,
    step2Internal1,
  ]);
}
